import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'

interface ConvertLeadConfigFormProps {
  title: string
  leadName: string
  leadCompany: string
  leadUuid: string
  matchedOrg?: { name: string; org_uuid: string } | null
  onSubmit: (message: string) => void
  isInteractive: boolean
}

const ORG_TYPES = [
  { value: 'PMC', label: 'PMC', desc: 'Property Management Company' },
  { value: 'OG', label: 'OG', desc: 'Ownership Group' },
  { value: 'PC', label: 'PC', desc: 'Parent Client' },
  { value: 'HP', label: 'HP', desc: 'Housing Property' },
  { value: 'AW', label: 'AW', desc: 'Agency Whitelabel' },
  { value: 'ORG', label: 'ORG', desc: 'General Organization' },
]

const VERTICALS = [
  'Multifamily',
  'Student',
  'Senior Living',
  'Real Estate',
  'Property Management',
  'Healthcare',
  'Hospitality',
  'Retail',
  'Technology',
  'Other',
]

const ConvertLeadConfigForm: React.FC<ConvertLeadConfigFormProps> = ({
  title,
  leadName,
  leadCompany,
  leadUuid,
  matchedOrg,
  onSubmit,
  isInteractive,
}) => {
  const [submitted, setSubmitted] = useState(false)

  // Org fields
  const [useExisting, setUseExisting] = useState(!!matchedOrg)
  const [orgType, setOrgType] = useState('ORG')
  const [orgName, setOrgName] = useState(leadCompany || '')
  const [legalEntityName, setLegalEntityName] = useState('')
  const [vertical, setVertical] = useState<string[]>([])

  const handleSubmit = () => {
    const parts: string[] = [
      `Convert lead "${leadName}" (lead_uuid: ${leadUuid}). Create organization and contact, then build the deal using the roadmap setup (skip_deal: true).`,
    ]

    if (useExisting && matchedOrg) {
      parts.push(`Use existing organization "${matchedOrg.name}" (org_uuid: ${matchedOrg.org_uuid}).`)
    } else {
      const orgTypeLabel = ORG_TYPES.find(t => t.value === orgType)?.desc || orgType
      parts.push(`Create new organization "${orgName}", type: ${orgType} (${orgTypeLabel}).`)
      if (vertical.length) parts.push(`Vertical: ${vertical.join(', ')}.`)
    }

    if (legalEntityName) parts.push(`Legal entity name: ${legalEntityName}.`)

    setSubmitted(true)
    onSubmit(parts.join(' '))
  }

  if (!isInteractive || submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 420,
          opacity: 0.8,
        }}
      >
        {submitted && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {submitted
            ? `Configured: ${orgName} (${orgType})`
            : title}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        my: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        maxWidth: 420,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
        {title}
      </Typography>

      {/* Organization Configuration */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {matchedOrg && (
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
                Found existing match:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant={useExisting ? 'contained' : 'outlined'}
                  onClick={() => setUseExisting(true)}
                  sx={{ fontSize: '0.7rem', textTransform: 'none', flex: 1 }}
                >
                  Use "{matchedOrg.name}"
                </Button>
                <Button
                  size="small"
                  variant={!useExisting ? 'contained' : 'outlined'}
                  onClick={() => setUseExisting(false)}
                  sx={{ fontSize: '0.7rem', textTransform: 'none', flex: 1 }}
                >
                  Create New
                </Button>
              </Box>
            </Box>
          )}

          {!useExisting && (
            <>
              <TextField
                size="small"
                label="Organization Name"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }}
              />

              <Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
                  Organization Type
                </Typography>
                <ToggleButtonGroup
                  value={orgType}
                  exclusive
                  onChange={(_, val) => val && setOrgType(val)}
                  size="small"
                  sx={{ flexWrap: 'wrap', gap: 0.5 }}
                >
                  {ORG_TYPES.map(t => (
                    <ToggleButton
                      key={t.value}
                      value={t.value}
                      sx={{
                        fontSize: '0.65rem',
                        py: 0.5,
                        px: 1,
                        textTransform: 'none',
                        borderRadius: '8px !important',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ fontWeight: 700 }}>{t.label}</Box>
                        <Box sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>{t.desc}</Box>
                      </Box>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Autocomplete
                multiple
                size="small"
                options={VERTICALS}
                value={vertical}
                onChange={(_, val) => setVertical(val)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...rest } = getTagProps({ index }) as any
                    return (
                      <Chip
                        key={key}
                        label={option}
                        size="small"
                        {...rest}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )
                  })
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Vertical"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }}
                  />
                )}
              />
            </>
          )}

          <TextField
            size="small"
            label="Legal Entity Name (optional)"
            value={legalEntityName}
            onChange={e => setLegalEntityName(e.target.value)}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }}
          />
        </Box>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSubmit}
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            borderColor: 'text.ai_agent_text',
            color: 'text.ai_agent_text',
            '&:hover': { borderColor: 'text.highlight', color: 'text.primary' },
          }}
        >
          Convert & Build Roadmap
        </Button>
      </Box>
    </Box>
  )
}

export default ConvertLeadConfigForm
